'use client';

import Image from 'next/image';
import Link from 'next/link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import { EmptyState, ProfileActivitySkeleton } from '@/components';
import { useT } from '@/hooks';
import { useUserActivity } from '@/services/query';
import { MarketActivity } from '@/types';
import { formatCurrency } from '@/utils';

import { EXTERNAL_LINKS } from '@/constants';

interface ProfileActivityTabProps {
  address: string;
}

export const ProfileActivityTab = ({ address }: ProfileActivityTabProps) => {
  const theme = useTheme();
  const { t } = useT();

  const {
    data: activityData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUserActivity(address, {
    enabled: !!address,
  });

  const allActivities = activityData?.pages.flatMap((page) => page) || [];

  if (isLoading) {
    return <ProfileActivitySkeleton />;
  }

  if (allActivities.length === 0) {
    return (
      <EmptyState
        title={t('leaderboardPortfolio.noActivity')}
        description={t('leaderboardPortfolio.noActivityDesc')}
      />
    );
  }

  // ── Shared helpers ────────────────────────────────────────────────
  const getItemMeta = (item: MarketActivity) => {
    const isBuy = item.side?.toUpperCase() === 'BUY';
    const shares = item.size?.toLocaleString() || '0';
    const amount = (item.size || 0) * (item.price || 0);
    const timeAgo = item.date
      ? formatDistanceToNow(new Date(item.date), { addSuffix: true }).replace('about ', '')
      : '';
    return { isBuy, shares, amount, timeAgo };
  };

  const sideChip = (isBuy: boolean) => (
    <Box
      sx={{
        display: 'inline-flex',
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        fontSize: '0.65rem',
        fontWeight: 800,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        bgcolor: isBuy
          ? alpha(theme.palette.success.main, 0.1)
          : alpha(theme.palette.error.main, 0.1),
        color: isBuy ? theme.palette.success.main : theme.palette.error.main,
        border: '1px solid',
        borderColor: isBuy
          ? alpha(theme.palette.success.main, 0.2)
          : alpha(theme.palette.error.main, 0.2),
      }}
    >
      {isBuy ? 'BUY' : 'SELL'}
    </Box>
  );

  const iconBox = (item: MarketActivity) => (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.text.primary, 0.03),
        border: '1px solid',
        borderColor: alpha(theme.palette.text.primary, 0.05),
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <Image
        src={item.icon || '/assets/icons/default-market.svg'}
        alt={item.title || 'Market'}
        fill
        style={{ objectFit: 'cover' }}
      />
    </Box>
  );

  const externalLink = (item: MarketActivity, timeAgo: string) => (
    <Link
      href={item.transactionHash ? EXTERNAL_LINKS.POLYGONSCAN_TX(item.transactionHash) : '#'}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: alpha(theme.palette.text.secondary, 0.4),
          fontWeight: 500,
          transition: 'color 0.2s',
          '&:hover': { color: 'text.primary' },
        }}
      >
        {timeAgo}
      </Typography>
      <OpenInNewIcon
        sx={{
          fontSize: 12,
          color: alpha(theme.palette.text.secondary, 0.2),
          transition: 'color 0.2s',
          '&:hover': { color: alpha(theme.palette.text.secondary, 0.5) },
        }}
      />
    </Link>
  );

  // ── Mobile card renderer ─────────────────────────────────────────
  const renderMobileCard = (item: MarketActivity, index: number) => {
    if (!item) return null;
    const { isBuy, shares, amount, timeAgo } = getItemMeta(item);

    return (
      <Box
        key={`mobile-${item.transactionHash || index}-${index}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
          gap: 2,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.06),
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
        }}
      >
        {/* Left: icon + title + subtitle */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          {iconBox(item)}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
            >
              {item.title || '-'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: alpha(theme.palette.text.secondary, 0.6), fontWeight: 500 }}
            >
              {item.side
                ? `${item.side.charAt(0).toUpperCase() + item.side.slice(1).toLowerCase()} `
                : ''}
              {shares} {t('leaderboardPortfolio.shares')}
            </Typography>
          </Box>
        </Box>

        {/* Right: amount + time + external link */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              fontSize: '0.925rem',
              whiteSpace: 'nowrap',
            }}
          >
            {formatCurrency(amount)}
          </Typography>
          {externalLink(item, timeAgo)}
        </Box>
      </Box>
    );
  };

  // ── Desktop row renderer ─────────────────────────────────────────
  const renderDesktopRow = (item: MarketActivity, index: number) => {
    if (!item) return null;
    const { isBuy, shares, amount, timeAgo } = getItemMeta(item);

    return (
      <TableRow
        key={`desktop-${item.transactionHash || index}-${index}`}
        sx={{
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
          '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
        }}
      >
        <TableCell sx={{ py: 2.5 }}>{sideChip(isBuy)}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {iconBox(item)}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 0.25,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                }}
              >
                {item.title || '-'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: alpha(theme.palette.text.secondary, 0.6), fontWeight: 500 }}
              >
                {shares} {t('leaderboardPortfolio.shares')}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ pr: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.925rem' }}
            >
              {formatCurrency(amount)}
            </Typography>
            {externalLink(item, timeAgo)}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const loadMoreButton = (colSpan: number, inTable: boolean) => {
    if (!hasNextPage) return null;
    const btn = (
      <Button
        onClick={() => fetchNextPage()}
        disabled={isFetchingNextPage}
        variant="outlined"
        size="small"
        sx={{
          color: 'text.secondary',
          borderColor: alpha(theme.palette.text.primary, 0.1),
          fontSize: '0.7rem',
          fontWeight: 700,
          textTransform: 'none',
          px: 3,
          borderRadius: 10,
          '&:hover': {
            borderColor: alpha(theme.palette.text.primary, 0.2),
            bgcolor: alpha(theme.palette.text.primary, 0.02),
            color: 'text.primary',
          },
        }}
      >
        {isFetchingNextPage ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={14} color="inherit" thickness={6} />
            {t('leaderboardPortfolio.loading')}
          </Box>
        ) : (
          t('leaderboardPortfolio.loadMore')
        )}
      </Button>
    );

    if (inTable) {
      return (
        <TableRow>
          <TableCell colSpan={colSpan} align="center" sx={{ py: 4, borderBottom: 'none' }}>
            {btn}
          </TableCell>
        </TableRow>
      );
    }
    return <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>{btn}</Box>;
  };

  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      {/* ── Mobile Card List (hidden on md+) ── */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.05),
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {allActivities.map(renderMobileCard)}
        {loadMoreButton(1, false)}
      </Box>

      {/* ── Desktop Table (hidden on xs) ── */}
      <TableContainer
        sx={{
          display: { xs: 'none', md: 'block' },
          maxHeight: 500,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.05),
          borderRadius: 2,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.text.primary, 0.05),
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                }}
              >
                {t('leaderboardPortfolio.side')}
              </TableCell>
              <TableCell
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                }}
              >
                {t('leaderboardPortfolio.market')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontWeight: 800,
                  fontSize: '0.625rem',
                  pt: 3,
                  pb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  pr: 3,
                  zIndex: 2,
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.text.primary, 0.05),
                }}
              >
                {t('leaderboardPortfolio.value')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allActivities.map(renderDesktopRow)}
            {loadMoreButton(3, true)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
